import { useEffect, useRef, useState } from 'react';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { uploadFile } from '../files/api';
import type { OutboundAttachment } from './api';

/**
 * Attaching a file, and recording a voice note (context.md §60–§65). Phase 71.
 *
 * Two affordances that look similar on the screen and are not the same thing at all:
 *
 * **Attaching** is a file the person already has. It goes through the ordinary upload — the MIME
 * allowlist, the extension agreement, the magic bytes, the tenant's quota — because an attachment
 * on a WhatsApp message is a file in the tenant's storage like any other, and giving it a second
 * path in would be giving it a second set of rules.
 *
 * **Recording** is a file that does not exist until somebody holds the button. The browser hands
 * over WebM/Opus, which is why the allowlist learned that type: building a recorder whose output
 * the storage refuses is building a broken recorder.
 *
 * The microphone is asked for **when the button is pressed**, never on mount. A page that asks for
 * the microphone as it loads is a page people deny permission to once and for all.
 */

/** What a recorder will actually produce here, best first. */
const RECORDING_TYPES = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm'];

export interface PendingAttachment extends OutboundAttachment {
  /** What to show while it is in the tray. */
  label: string;
  /** Set while the bytes are still going up. */
  uploading?: boolean;
}

function recordingType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;

  return RECORDING_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

export function AttachmentTray({
  items,
  onRemove,
}: {
  items: readonly PendingAttachment[];
  onRemove: (fileId: string) => void;
}): React.JSX.Element | null {
  if (items.length === 0) return null;

  return (
    <ul className="attachments">
      {items.map((item) => (
        <li key={item.fileId} className={item.uploading ? 'is-uploading' : undefined}>
          <span className="attachments__name">{item.label}</span>
          {item.uploading ? (
            <span className="muted small">{t('inbox.attaching')}</span>
          ) : (
            <button
              type="button"
              className="attachments__remove"
              aria-label={t('inbox.removeAttachment')}
              onClick={() => onRemove(item.fileId)}
            >
              ×
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The paperclip.
 *
 * `accept` is left off deliberately: the server decides what it will take, and a browser filter
 * that disagrees with it is a filter that hides a file somebody is allowed to send. A refusal comes
 * back from the upload with a reason, which is the honest place for it.
 */
export function AttachButton({
  disabled,
  onAttached,
  onError,
}: {
  disabled: boolean;
  onAttached: (attachment: PendingAttachment) => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function take(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;

    setBusy(true);

    for (const file of Array.from(files)) {
      try {
        const stored = await uploadFile(file, { visibility: 'PRIVATE' });

        onAttached({
          fileId: stored.id,
          filename: file.name,
          mimeType: file.type || undefined,
          label: file.name,
        });
      } catch (caught) {
        onError(messageFor(caught));
      }
    }

    setBusy(false);

    // So the same file can be chosen twice in a row, which otherwise silently does nothing.
    if (input.current) input.current.value = '';
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(event) => void take(event.target.files)}
      />
      <button
        type="button"
        className="composer__icon"
        disabled={disabled || busy}
        title={t('inbox.attach')}
        aria-label={t('inbox.attach')}
        onClick={() => input.current?.click()}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path
            d="M21 11.5 12.5 20a5.5 5.5 0 0 1-7.8-7.8l8.5-8.5a3.7 3.7 0 1 1 5.2 5.2L10 17.3a1.8 1.8 0 1 1-2.6-2.6l7.9-7.9"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  );
}

/**
 * The microphone.
 *
 * Press to start, press again to stop and send it to the tray. Not press-and-hold: holding a mouse
 * button for a minute is not the same gesture as holding a phone, and letting go by accident half
 * way through a sentence loses the recording.
 *
 * The stream's tracks are stopped explicitly when recording ends. Without that the browser's
 * recording indicator stays lit and the microphone stays open, which is alarming and correct to be
 * alarmed by.
 */
export function RecordButton({
  disabled,
  onRecorded,
  onError,
}: {
  disabled: boolean;
  onRecorded: (attachment: PendingAttachment) => void;
  onError: (message: string) => void;
}): React.JSX.Element | null {
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);

  const supported = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices);

  useEffect(() => {
    if (!recording) return;

    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);

    return () => window.clearInterval(timer);
  }, [recording]);

  // A tab closed mid-recording must not leave the microphone open.
  useEffect(
    () => () => {
      recorder.current?.stream.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  async function start(): Promise<void> {
    const type = recordingType();

    if (!type) {
      onError(t('inbox.recordUnsupported'));

      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const instance = new MediaRecorder(stream, { mimeType: type });

      chunks.current = [];
      instance.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data);
      };

      instance.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        void finish(type);
      };

      recorder.current = instance;
      instance.start();
      setSeconds(0);
      setRecording(true);
    } catch {
      // Denied, or no microphone. Both are the same thing from here: no recording.
      onError(t('inbox.recordDenied'));
    }
  }

  async function finish(type: string): Promise<void> {
    setRecording(false);
    setBusy(true);

    try {
      // The stored type has no codec suffix: `audio/webm;codecs=opus` is how it was recorded,
      // `audio/webm` is what it is.
      const mimeType = type.split(';')[0]!;
      const extension = mimeType === 'audio/ogg' ? 'ogg' : 'webm';
      const blob = new Blob(chunks.current, { type: mimeType });
      const name = `audio-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}.${extension}`;
      const stored = await uploadFile(new File([blob], name, { type: mimeType }), {
        visibility: 'PRIVATE',
      });

      onRecorded({
        fileId: stored.id,
        filename: name,
        mimeType,
        // What makes it a voice note on the customer's phone rather than a file to download.
        voice: true,
        label: t('inbox.voiceNote'),
      });
    } catch (caught) {
      onError(messageFor(caught));
    } finally {
      chunks.current = [];
      setBusy(false);
    }
  }

  // No microphone API at all — an old browser, or a page not served over https.
  if (!supported) return null;

  return (
    <button
      type="button"
      className={`composer__icon${recording ? ' is-recording' : ''}`}
      disabled={disabled || busy}
      title={recording ? t('inbox.recordStop') : t('inbox.record')}
      aria-label={recording ? t('inbox.recordStop') : t('inbox.record')}
      onClick={() => (recording ? recorder.current?.stop() : void start())}
    >
      {recording ? (
        <span className="composer__recording">
          <span className="composer__recording-dot" aria-hidden="true" />
          {String(Math.floor(seconds / 60)).padStart(2, '0')}:
          {String(seconds % 60).padStart(2, '0')}
        </span>
      ) : (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
          <path
            d="M5 11a7 7 0 0 0 14 0M12 18v3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}
